import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit
} from '@angular/core';
import {
  CityPacket,
  Constants,
  ContactPropertySearcherType,
  CustomerSettings,
  GenericFormGroup,
  GlobalCustomQuestions,
  Profession,
  SaveContactData,
  SearchProfile,
  SearchProfilePatchedData,
  SelfRegistrationConfig
} from '@ui/shared/models';
import moment from 'moment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  dateRangeValidator,
  maxDateValidator,
  minDateValidator,
  zipCodeValidator
} from '@ui/legacy-lib';
import { Observable, timer } from 'rxjs';
import {
  EditMemberStepsRoutes,
  EditMemberWizardSteps
} from 'landlord/main/screens/internal-list/components/edit-member/edit-member.child.routes';
import { Step } from '@ui/legacy-lib';
import { ActivatedRoute } from '@angular/router';
import { BasicStepHandler } from '@ui/legacy-lib';
import { Store } from '@ngrx/store';
import {
  FetchConstants,
  getConstants,
  getCurrentLocale,
  Go,
  ShowInfo
} from '@ui/legacy-lib';
import { ActionState } from '@ui/legacy-lib';
import { filter, scan, take, takeWhile, tap } from 'rxjs/operators';
import { customQuestionsPayloadHelper } from '@ui/legacy-lib';
import {
  DistrictHandlingService,
  HierarchicalQuestionService,
  SearchProfileService
} from '@ui/legacy-lib';
import {
  GlobalCustomQuestionsComponent,
  ModalService,
  ProfileDkTwoComponent,
  WizardComponent,
  WizardStepDirective
} from '@ui/legacy-lib';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  ApplicationConfirmationComponent,
  CardComponent
} from '@ui/legacy-lib';
import { DateTimePipe } from '@ui/legacy-lib';
import {
  MemberBasicInformationComponent,
  MemberSearchProfileComponent
} from 'landlord/components/contacts-components';
import {
  ResetSelfRegistrationActionState,
  SelfRegisterOfflineContact
} from 'landlord/main/+state/offline-self-registration/offline-self-registration.action';
import { offlineSelfRegistrationFeature } from 'landlord/main/+state/offline-self-registration/offline-self-registration.reducers';

const COUNT_DOWN_TIME = 60 * 10; // 10 Minutes

@UntilDestroy()
@Component({
  selector: 'app-offline-self-registration-wizard',
  templateUrl: './offline-self-registration-wizard.component.html',
  styleUrls: ['./offline-self-registration-wizard.component.scss'],
  providers: [SearchProfileService, DistrictHandlingService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    TranslateModule,
    CardComponent,
    DateTimePipe,
    WizardComponent,
    MemberBasicInformationComponent,
    MemberSearchProfileComponent,
    GlobalCustomQuestionsComponent,
    ProfileDkTwoComponent,
    ApplicationConfirmationComponent,
    WizardStepDirective
  ]
})
@BasicStepHandler(EditMemberStepsRoutes)
export class OfflineSelfRegistrationWizardComponent implements OnInit {
  /* eslint-disable @typescript-eslint/ban-types */
  public updateSelectable: Function;
  public getStepIndex: Function;
  public selectStep: Function;
  public showStep: Function;
  public nextStep: Function;
  public previousStep: Function;
  public navigateToStep: Function;
  public checkRouteValidity: Function;
  public preserveStepsData: Function;
  public getAllSteps: Function;
  /* eslint-enable @typescript-eslint/ban-types */

  public constants$: Observable<Constants>;
  public selfRegisterOfflineContactActionState$: Observable<ActionState>;
  public form: FormGroup;
  public disableActions = false;
  public inviteToRegister = true;
  public allSteps: Step[];
  public completedSteps: string[] = [];
  public visitedSteps: string[] = [];
  public currentStepIndex = 0;
  public selfRegistrationConfig: SelfRegistrationConfig;
  public timer$: Observable<number>;
  public isLastMinute: boolean;
  public customerSettings$: Observable<CustomerSettings>;
  public landlordCityPacket: CityPacket;
  public onlyShowConfiguredCityPartsToUser = false;
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private hierarchicalQuestionService = inject(HierarchicalQuestionService);
  private searchProfileService = inject(SearchProfileService);
  private modalService = inject(ModalService);
  private districtHandlingService = inject(DistrictHandlingService);

  public get currentForm() {
    return this.form.get(
      this.allSteps[this.currentStepIndex].name
    ) as FormGroup;
  }

  public get showGlobalQuestions() {
    const { questions, customQuestions } =
      this.selfRegistrationConfig?.questionsContainer;
    return questions?.length > 0 || customQuestions?.length > 0;
  }

  ngOnInit(): void {
    this.store.dispatch(new FetchConstants());
    this.constants$ = this.store.select(getConstants);
    this.selfRegisterOfflineContactActionState$ = this.store.select(
      offlineSelfRegistrationFeature.selectSelfRegisterOfflineContactActionState
    );
    this.form = this.formValues;

    this.store
      .select(getCurrentLocale)
      .pipe(untilDestroyed(this))
      .subscribe(currentLocale => {
        this.form
          .get('memberInformation.preferredLanguage')
          .patchValue(currentLocale);
      });

    this.allSteps = this.getAllSteps();

    this.startTimer();

    this.store
      .select(offlineSelfRegistrationFeature.selectSelfRegistrationConfig)
      .pipe(
        filter(config => !!config),
        take(1)
      )
      .subscribe(config => {
        this.selfRegistrationConfig = {
          ...config,
          questionsContainer: {
            customQuestions: config.questionsContainer.customQuestions,
            questions: config.questionsContainer.questions.map(
              q => q.rootQuestion
            ) as any
          }
        };

        if (this.showGlobalQuestions) {
          this.form.get('globalCustomQuestions').patchValue({
            hierarchicalRootQuestions:
              this.selfRegistrationConfig.questionsContainer.questions,
            customQuestionResponses:
              this.selfRegistrationConfig.questionsContainer.customQuestions
          });
        } else {
          this.allSteps = this.getAllSteps(
            EditMemberWizardSteps.GLOBAL_CUSTOM_QUESTIONS
          );
        }

        if (this.selfRegistrationConfig.defaultDistrictGroup) {
          this.onlyShowConfiguredCityPartsToUser = true;
          this.landlordCityPacket =
            this.districtHandlingService.cityPacketViewPayload([
              this.selfRegistrationConfig.defaultDistrictGroup
            ])[0];
        }
      });
  }

  private get formValues() {
    const profession: GenericFormGroup<Profession> = this.fb.group({
      type: null,
      subType: null,
      income: 0
    }) as GenericFormGroup<Profession>;
    const minDateOfBirth = moment('1900-01-01', 'YYYY-MM-DD').toDate();

    const housingPermission = this.fb.group({
      type: [null],
      amountPeople: [null]
    });

    const memberInformation = this.fb.group({
      id: [''],
      data: this.fb.group({
        firstname: ['', Validators.required],
        name: ['', Validators.required],
        phone: [''],
        birthday: [
          null,
          Validators.compose([
            maxDateValidator(moment().toDate()),
            minDateValidator(minDateOfBirth)
          ])
        ],
        entryDate: ['', dateRangeValidator('leavingDate', true)],
        leavingDate: ['', dateRangeValidator('entryDate', false)]
      }),
      membershipNumber: [''],
      email: ['', Validators.compose([Validators.required, Validators.email])],
      address: this.fb.group({
        city: [''],
        zipCode: [
          '',
          {
            validators: zipCodeValidator,
            updateOn: 'blur'
          }
        ],
        district: [''],
        street: [''],
        houseNumber: [''],
        region: [''],
        country: ['DE']
      }),
      propertySearcherType: [ContactPropertySearcherType.OFFLINE],
      housingPermission,
      moveInDate: null,
      preferredLanguage: [null, Validators.required]
    });

    const dkTwo = this.fb.group({
      householdType: null,
      residents: null,
      dateOfBirth: [
        null,
        Validators.compose([
          maxDateValidator(moment().toDate()),
          minDateValidator(minDateOfBirth)
        ])
      ],
      profession,
      animals: null,
      guarantorExist: null,
      furtherInformation: null
    });

    const searchProfiles = this.fb.array([]);

    const globalCustomQuestions: GenericFormGroup<GlobalCustomQuestions> =
      this.fb.group({
        customQuestionResponses: [],
        hierarchicalRootQuestions: []
      });

    return this.fb.group({
      memberInformation,
      searchProfiles,
      globalCustomQuestions,
      dkTwo
    }) as GenericFormGroup;
  }

  public save() {
    const { data, housingPermission, moveInDate, ...restMember } =
      this.form.getRawValue().memberInformation;
    const { animals, ...restProfile } = this.form.value.dkTwo;
    const searchProfiles = this.form.getRawValue()
      .searchProfiles as SearchProfile[];
    const { customQuestionResponses, hierarchicalRootQuestions } =
      this.form.value.globalCustomQuestions;

    const extractedSearchProfiles = searchProfiles.map(
      ({ createdByPS, ...restSearchProfile }) => {
        const hasSearchProfileId =
          restSearchProfile.id !== 'randomId' && restSearchProfile.id !== '';
        return this.searchProfileService.extractSearchProfileDataPayload(
          restSearchProfile as SearchProfilePatchedData,
          hasSearchProfileId ? restSearchProfile.id : null
        );
      }
    );

    const payload = {
      ...restMember,
      data,
      sensitiveDataAccessible: true,
      offlineData: {
        userProfileData: {
          moveInDate,
          ...restProfile,
          additionalInformation: {
            housingPermission,
            animals
          }
        }
      },
      searchProfiles: extractedSearchProfiles,
      token: this.route.snapshot.queryParams.token
    } as SaveContactData;

    if (customQuestionResponses?.length) {
      payload.offlineData.customQuestionResponses =
        customQuestionsPayloadHelper(
          customQuestionResponses,
          this.selfRegistrationConfig.questionsContainer.customQuestions
        );
    }

    if (hierarchicalRootQuestions?.length) {
      payload.offlineData.questionResponses =
        this.hierarchicalQuestionService.getHierarchicalRootQuestionPayload(
          hierarchicalRootQuestions
        );
    }

    this.store.dispatch(SelfRegisterOfflineContact({ payload }));
  }

  public goToNextStep(event) {
    this.startTimer();
    this.nextStep(event);
  }

  public goToPrevStep(event) {
    this.startTimer();
    this.previousStep(event);
  }

  private startTimer() {
    this.timer$ = timer(0, 1000).pipe(
      scan(acc => --acc, COUNT_DOWN_TIME),
      takeWhile(x => x >= 0),
      tap(counter => {
        this.isLastMinute = false;
        if (counter < 60) this.isLastMinute = true;
        if (counter === 0) {
          this.onFormCancellation();
        }
      })
    );
  }

  public toIntroPage() {
    this.store.dispatch(
      new Go({
        path: ['selfRegistration/intro'],
        extras: { queryParamsHandling: 'preserve' }
      })
    );

    this.store.dispatch(ResetSelfRegistrationActionState());
  }

  public cancelFlow() {
    this.modalService
      .openConfirmation({
        data: {
          titleMessage: 'self_registration.cancel.modal.title_L',
          message: 'self_registration.cancel.modal.text_L'
        }
      })
      .onClose()
      .subscribe(() => {
        this.onFormCancellation();
      });
  }

  private onFormCancellation() {
    this.store.dispatch(new ShowInfo('self_registration.cancel.toast_l'));
    this.toIntroPage();
  }
}
